import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  Box,
  Chip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

function AppointmentTable({ 
  data, 
  isLoading, 
  totalRecords, 
  page, 
  setPage, 
  rowsPerPage, 
  setRowsPerPage 
}) {
  const [searchQuery, setSearchQuery] = React.useState("");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when limit changes
  };

  // Frontend search filter applied to the current paginated dataset
  const filteredData = React.useMemo(() => {
    if (!data) return [];
    if (!searchQuery) return data;
    
    const lowerQuery = searchQuery.toLowerCase();
    return data.filter((row) => {
      const patientName = `${row.patient?.firstName || ""} ${row.patient?.lastName || ""}`.toLowerCase();
      const doctorName = `${row.doctor?.firstName || ""} ${row.doctor?.lastName || ""}`.toLowerCase();
      const status = (row.appointmentStatus || "").toLowerCase();
      
      return patientName.includes(lowerQuery) || doctorName.includes(lowerQuery) || status.includes(lowerQuery);
    });
  }, [data, searchQuery]);

  return (
    <Box>
      {/* Search Bar */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          size="small"
          placeholder="Search current page..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: '100%', sm: '300px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
        <Table sx={{ minWidth: 650 }} aria-label="appointments history table">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Doctor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Loading history...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredData.length > 0 ? (
              filteredData.map((row) => (
                <TableRow key={row._id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell>
                    {row.startDateTime
                      ? new Date(row.startDateTime).toLocaleDateString(undefined, {
                          year: "numeric", month: "short", day: "numeric",
                        })
                      : "N/A"}
                  </TableCell>
                  <TableCell>{`${row.startTime || ""} - ${row.endTime || ""}`}</TableCell>
                  <TableCell>
                    {row.patient ? `${row.patient.firstName || ""} ${row.patient.lastName || ""}` : "N/A"}
                  </TableCell>
                  <TableCell>
                    {row.doctor ? `${row.doctor.firstName || ""} ${row.doctor.lastName || ""}` : "N/A"}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.appointmentStatus || "N/A"} 
                      color={row.appointmentStatus === "completed" ? "success" : "default"}
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No records found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        {/* Pagination Controls */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default AppointmentTable;